import { FC } from "react";

import slugify from "react-slugify";

interface TextFieldProps {
  /**
   * The type of autocomplete behavior for the text field.
   * - 'on' for general autocomplete.
   * - 'name' for autocomplete of a person's name.
   * - 'given-name' for autocomplete of a person's given name.
   * - 'family-name' for autocomplete of a person's family name.
   * - 'email' for autocomplete of an email address.
   * - 'organization-title' for autocomplete of an organization title.
   * - 'organization' for autocomplete of an organization name.
   * - 'street-address' for autocomplete of a street address.
   * - 'country-name' for autocomplete of a country name.
   * - 'postal-code' for autocomplete of a postal code.
   * - 'language' for autocomplete of a language.
   * - 'url' for autocomplete of a URL.
   * - 'tel' for autocomplete of a telephone number.
   */
  autoComplete?:
    | "other"
    | "on"
    | "name"
    | "given-name"
    | "family-name"
    | "email"
    | "organization-title"
    | "organization"
    | "street-address"
    | "country-name"
    | "postal-code"
    | "language"
    | "url"
    | "tel";
  /**
   * The ID of the text field.
   */
  id?: string;
  /**
   * The label for the text field.
   */
  label: string;
  /**
   * Specifies whether the text field is required.
   */
  required?: boolean;
  /**
   * The variant of the text field.
   * - 'text' for a single-line text input.
   * - 'textarea' for a multi-line text input.
   */
  variant?: "text" | "textarea";
}
export const TextField: FC<TextFieldProps> = ({
  autoComplete = "on",
  id,
  label,
  required = false,
  variant = "text",
}) => {
  const Tag = variant === "text" ? "input" : "textarea";
  const renderedId = id || slugify(label);

  const getTextType = () => {
    if (variant !== "text") return undefined;
    if (autoComplete === "email") return autoComplete;
    if (autoComplete === "tel") return autoComplete;
    return "text";
  };

  // Failsafe for when autoComplete is "other" in Sanity
  const internalAutoComplete = autoComplete === "other" ? "on" : autoComplete;

  return (
    <div>
      <label className="text-base font-bold xl:text-xl " htmlFor={renderedId}>
        {`${label}${!required ? " (valfritt)" : ""}`}
      </label>
      <Tag
        autoComplete={variant === "text" ? internalAutoComplete : undefined}
        className="flex w-full rounded-[4px] border-2 border-gray-500 px-1 py-2 focus-visible:outline focus-visible:outline-4 focus-visible:outline-darkerblue dark:focus-visible:outline-white xl:px-2 xl:py-4"
        id={renderedId}
        name={renderedId}
        required={required}
        type={getTextType()}
      />
    </div>
  );
};
export default TextField;
